/**
 * สต๊อกคงเหลือ (Stock On Hand) และความเคลื่อนไหวสต๊อก (Inventory Ledger) — D-114
 *
 * อ่านอย่างเดียวทั้งสองหน้า (`INVENTORY` + VIEW ที่ระดับ route) ไม่มีปุ่มเขียน และ **ไม่มีคอลัมน์ "ใช้ได้"**:
 * เซิร์ฟเวอร์รายงานข้อเท็จจริงสองอย่าง (จำนวนและสถานะคุณภาพของล็อต) แล้วให้ผู้ดูตัดสินเอง
 * จำนวนติดลบแสดงตามจริงเป็นสีเตือน — เป็นคำตอบ (ledger บอกว่าออกมากกว่าเข้า) ไม่ใช่ความผิดพลาดของหน้าจอ
 */

import { useCallback, useRef, useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { qualityStatusLabel, qualityStatusTone } from '../goodsReceipts/goodsReceiptView';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  fetchInventoryLedger,
  fetchStockBalances,
  stockFailureText,
  type LedgerMovement,
  type LedgerQuery,
  type StockBalance,
  type StockBalanceQuery,
} from '../../lib/inventory/stock';
import { selectablePlants } from '../productionOrders/productionOrderView';

const PAGE_SIZE = 100;

export interface StockViewProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

function bangkokDay(offsetDays = 0): string {
  const moment = new Date(Date.now() + offsetDays * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(moment);
}

function QtyCell({ qty }: { readonly qty: string }) {
  return (
    <td
      className="cap-table__num"
      style={qty.trim().startsWith('-') ? { color: '#b0182c', fontWeight: 600 } : undefined}
      data-negative={qty.trim().startsWith('-') ? 'true' : 'false'}
    >
      {qty}
    </td>
  );
}

function PlantSelect({
  plants,
  value,
  onChange,
}: {
  readonly plants: readonly string[];
  readonly value: string;
  readonly onChange: (plant: string) => void;
}) {
  return (
    <label className="cap-field">
      <span className="cap-field__label">โรงงาน</span>
      <select className="cap-input" aria-label="โรงงาน" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— เลือกโรงงาน —</option>
        {plants.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </label>
  );
}

function useDefaultPlant(plants: readonly string[]) {
  const [chosen, setChosen] = useState('');
  const plant = chosen !== '' ? chosen : plants.length === 1 ? (plants[0] ?? '') : '';
  return [plant, setChosen] as const;
}

/* ------------------------------------------------------------ Stock On Hand -- */

export function StockOnHandScreen({ permissions, apiBaseUrl, fetchImpl }: StockViewProps) {
  const plants = selectablePlants(permissions);
  const [plant, setPlant] = useDefaultPlant(plants);
  const [location, setLocation] = useState('');
  const [product, setProduct] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [rows, setRows] = useState<readonly StockBalance[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  // ตัวกรองที่ใช้ค้นหาครั้งล่าสุด: "โหลดเพิ่ม" ต้องใช้ตัวกรองชุดเดียวกับที่ออก cursor ไม่ใช่ค่าที่ผู้ใช้เพิ่งพิมพ์แก้
  const lastQuery = useRef<StockBalanceQuery | null>(null);

  const load = useCallback(
    async (next: string | null) => {
      setBusy(true);
      setError(null);
      if (next === null) {
        setRows([]);
        setCursor(null);
        lastQuery.current = {
          plant_code: plant,
          storage_location_code: location,
          product_code: product,
          include_zero: includeZero || undefined,
          limit: PAGE_SIZE,
        };
      }
      const result = await fetchStockBalances(
        { ...(lastQuery.current as StockBalanceQuery), ...(next === null ? {} : { cursor: next }) },
        options,
      );
      setBusy(false);
      if (result.status !== 'ready') {
        setError(stockFailureText(result));
        return;
      }
      setRows((current) => (next === null ? result.page.items : [...current, ...result.page.items]));
      setCursor(result.page.next_cursor);
      setLoaded(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plant, location, product, includeZero, apiBaseUrl, fetchImpl],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (plant === '') {
      setError('เลือกโรงงาน');
      return;
    }
    void load(null);
  };

  return (
    <div className="cap-page" data-testid="stock-on-hand">
      <div className="cap-page__head">
        <h1 className="cap-page__title">สต๊อกคงเหลือ (Stock On Hand)</h1>
      </div>
      <form className="cap-filters" onSubmit={submit}>
        <PlantSelect plants={plants} value={plant} onChange={setPlant} />
        <label className="cap-field">
          <span className="cap-field__label">ที่เก็บ</span>
          <input className="cap-input" aria-label="ที่เก็บ" value={location} onChange={(e) => setLocation(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">รหัสสินค้า</span>
          <input className="cap-input" aria-label="รหัสสินค้า" value={product} onChange={(e) => setProduct(e.target.value)} />
        </label>
        <label className="cap-field">
          <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} /> แสดงยอดศูนย์
        </label>
        <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
          {busy ? 'กำลังโหลด…' : 'ค้นหา'}
        </button>
      </form>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="stock-error">
          {error}
        </p>
      )}
      {loaded && (
        <table className="cap-table" data-testid="stock-table">
          <thead>
            <tr>
              <th>ที่เก็บ</th>
              <th>สินค้า</th>
              <th>Lot</th>
              <th>สถานะคุณภาพ</th>
              <th style={{ textAlign: 'right' }}>จำนวน</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5}>ไม่พบสต๊อกตามเงื่อนไข</td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={`${row.storage_location_code}|${row.product_code}|${row.lot_id}`}>
                <td>{row.storage_location_code}</td>
                <td>
                  <code className="cap-table__code">{row.product_code}</code>
                </td>
                <td>
                  <a href={`/traceability/lots?lot_id=${encodeURIComponent(row.lot_id)}`}>{row.lot_id.slice(0, 8)}</a>
                </td>
                <td>
                  <StatusBadge tone={qualityStatusTone(row.quality_status)}>
                    {qualityStatusLabel(row.quality_status)}
                  </StatusBadge>
                </td>
                <QtyCell qty={row.qty} />
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cursor !== null && (
        <button type="button" className="cap-button cap-button--ghost" disabled={busy} onClick={() => void load(cursor)}>
          โหลดเพิ่ม
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------- Inventory Ledger -- */

export function InventoryLedgerScreen({ permissions, apiBaseUrl, fetchImpl }: StockViewProps) {
  const plants = selectablePlants(permissions);
  const [plant, setPlant] = useDefaultPlant(plants);
  const [dateFrom, setDateFrom] = useState(bangkokDay(-6));
  const [dateTo, setDateTo] = useState(bangkokDay(0));
  const [location, setLocation] = useState('');
  const [product, setProduct] = useState('');
  const [movementType, setMovementType] = useState('');
  const [rows, setRows] = useState<readonly LedgerMovement[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  const lastQuery = useRef<LedgerQuery | null>(null);

  const load = useCallback(
    async (next: string | null) => {
      setBusy(true);
      setError(null);
      if (next === null) {
        setRows([]);
        setCursor(null);
        lastQuery.current = {
          plant_code: plant,
          date_from: dateFrom,
          date_to: dateTo,
          storage_location_code: location,
          product_code: product,
          movement_type: movementType,
          limit: PAGE_SIZE,
        };
      }
      const result = await fetchInventoryLedger(
        { ...(lastQuery.current as LedgerQuery), ...(next === null ? {} : { cursor: next }) },
        options,
      );
      setBusy(false);
      if (result.status !== 'ready') {
        setError(stockFailureText(result));
        return;
      }
      setRows((current) => (next === null ? result.page.items : [...current, ...result.page.items]));
      setCursor(result.page.next_cursor);
      setLoaded(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plant, dateFrom, dateTo, location, product, movementType, apiBaseUrl, fetchImpl],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (plant === '') {
      setError('เลือกโรงงาน');
      return;
    }
    if (dateFrom === '' || dateTo === '' || dateFrom > dateTo) {
      setError('ช่วงวันที่ไม่ถูกต้อง (ไม่เกิน 92 วัน)');
      return;
    }
    void load(null);
  };

  return (
    <div className="cap-page" data-testid="inventory-ledger">
      <div className="cap-page__head">
        <h1 className="cap-page__title">ความเคลื่อนไหวสต๊อก (Inventory Ledger)</h1>
      </div>
      <form className="cap-filters" onSubmit={submit}>
        <PlantSelect plants={plants} value={plant} onChange={setPlant} />
        <label className="cap-field">
          <span className="cap-field__label">ตั้งแต่วันที่</span>
          <input type="date" className="cap-input" aria-label="ตั้งแต่วันที่" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ถึงวันที่</span>
          <input type="date" className="cap-input" aria-label="ถึงวันที่" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ที่เก็บ</span>
          <input className="cap-input" aria-label="ที่เก็บ" value={location} onChange={(e) => setLocation(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">รหัสสินค้า</span>
          <input className="cap-input" aria-label="รหัสสินค้า" value={product} onChange={(e) => setProduct(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ประเภทการเคลื่อนไหว</span>
          <input className="cap-input" aria-label="ประเภทการเคลื่อนไหว" value={movementType} onChange={(e) => setMovementType(e.target.value)} />
        </label>
        <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
          {busy ? 'กำลังโหลด…' : 'ค้นหา'}
        </button>
      </form>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="ledger-error">
          {error}
        </p>
      )}
      {loaded && (
        <table className="cap-table" data-testid="ledger-table">
          <thead>
            <tr>
              <th>เวลา</th>
              <th>ที่เก็บ</th>
              <th>สินค้า</th>
              <th>Lot</th>
              <th>ประเภท</th>
              <th>เอกสารอ้างอิง</th>
              <th style={{ textAlign: 'right' }}>จำนวน</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}>ไม่พบความเคลื่อนไหวในช่วงที่เลือก</td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.ledger_id}>
                <td>{row.created_at}</td>
                <td>{row.storage_location_code}</td>
                <td>
                  <code className="cap-table__code">{row.product_code}</code>
                </td>
                <td>
                  <a href={`/traceability/lots?lot_id=${encodeURIComponent(row.lot_id)}`}>{row.lot_id.slice(0, 8)}</a>
                </td>
                <td>{row.movement_type}</td>
                <td>
                  {row.reference_doc_type} <span className="cap-table__muted">{row.reference_doc_id.slice(0, 8)}</span>
                </td>
                <QtyCell qty={row.qty} />
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cursor !== null && (
        <button type="button" className="cap-button cap-button--ghost" disabled={busy} onClick={() => void load(cursor)}>
          โหลดเพิ่ม
        </button>
      )}
    </div>
  );
}
